import { Card, CardContent, Typography } from '@pesto/ui';
import { LoadingSpinner } from '@pesto/ui/components/LoadingSpinner';
import { CreditCard } from 'lucide-react';

import { EntityHeader } from '../../EntityProfile/components/EntityHeader';
import { calculateUtilizationPercentage } from '../utils/creditUtilizationCalc';

interface CreditUtilizationProps {
  limit: string;
  balance: string;
  availableAmount: string;
  spent: string;
  remaining: string;
  loading?: boolean;
  isDelinquent?: boolean | null;
  daysDelinquent?: number | null;
  delinquentSince?: string | null;
}

export const CreditUtilizationBar = ({ spent, limit }: { spent: string; limit: string }) => {
  const utilizationPercentage = calculateUtilizationPercentage(spent, limit);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Typography variant="body" className="text-sm font-medium">
          Credit Utilization
        </Typography>
        <Typography variant="body" className="text-sm font-semibold">
          {utilizationPercentage.toFixed(1)}%
        </Typography>
      </div>
      <div className="bg-muted border-muted-foreground/10 h-2 w-full rounded-full border-[0.5px]">
        <div
          className="bg-primary h-full rounded-full transition-all duration-500 ease-in-out"
          style={{ width: `${utilizationPercentage}%` }}
        />
      </div>
    </div>
  );
};

export const CreditUtilization = ({
  limit,
  balance,
  availableAmount,
  spent,
  remaining,
  loading,
  isDelinquent,
  daysDelinquent,
  delinquentSince,
}: CreditUtilizationProps) => {
  if (loading) {
    return (
      <Card>
        <CardContent className="flex h-48 items-center justify-center">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  const utilizationPercentage = calculateUtilizationPercentage(spent, limit);

  return (
    <Card>
      <EntityHeader
        config={{
          avatar: { icon: CreditCard },
          title: 'Credit Utilization',
          description: 'Monitor your credit usage and availability',
        }}
      />

      <CardContent className="space-y-6">
        <div className="bg-muted/50 rounded-lg border p-4">
          <CreditUtilizationBar limit={limit} spent={spent} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-muted/30 rounded-lg border p-4">
            <div className="space-y-2">
              <Typography variant="body" className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                Available Credit
              </Typography>
              <Typography variant="headerSmall" className="text-2xl font-bold">
                {availableAmount}
              </Typography>
              <Typography variant="body" className="text-muted-foreground text-sm">
                of {limit} limit
              </Typography>
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg border p-4">
            <div className="space-y-2">
              <Typography variant="body" className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                Current Balance
              </Typography>
              <Typography variant="headerSmall" className="text-2xl font-bold">
                {balance}
              </Typography>
              <Typography variant="body" className="text-muted-foreground text-sm">
                {utilizationPercentage.toFixed(1)}% utilized
              </Typography>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Typography variant="body" className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
            Detailed Breakdown
          </Typography>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="bg-muted/20 flex items-center justify-between rounded-lg border p-3">
              <Typography variant="body" className="text-muted-foreground text-sm">
                Amount Spent
              </Typography>
              <Typography variant="body" className="text-sm font-semibold">
                {spent}
              </Typography>
            </div>
            <div className="bg-muted/20 flex items-center justify-between rounded-lg border p-3">
              <Typography variant="body" className="text-muted-foreground text-sm">
                Remaining
              </Typography>
              <Typography variant="body" className="text-sm font-semibold">
                {remaining}
              </Typography>
            </div>
            <div className="bg-muted/20 flex items-center justify-between rounded-lg border p-3">
              <Typography variant="body" className="text-muted-foreground text-sm">
                Credit Limit
              </Typography>
              <Typography variant="body" className="text-sm font-semibold">
                {limit}
              </Typography>
            </div>
          </div>
        </div>
        {isDelinquent && (
          <div className="border-destructive/20 bg-destructive/10 rounded-lg border p-4">
            <Typography variant="headerSmall" className="font-bold">
              Delinquency
            </Typography>
            <Typography variant="body" className="text-sm">
              This user is currently <span className="font-semibold">delinquent</span>.
            </Typography>
            {daysDelinquent != null && (
              <Typography variant="body" className="text-sm">
                Days Delinquent: <span className="font-semibold">{daysDelinquent}</span>
              </Typography>
            )}
            {delinquentSince && (
              <Typography variant="body" className="text-sm">
                Delinquent Since:{' '}
                <span className="font-semibold">{new Date(delinquentSince).toLocaleDateString()}</span>
              </Typography>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
